import React, { useCallback, useRef, useState } from 'react';
import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay
} from '@chakra-ui/react';
import { AiOutlineCloudUpload } from 'react-icons/ai';
import { FaFile, FaTrash } from 'react-icons/fa';
import { twMerge } from 'tailwind-merge';
import { ApiSpecState } from '../F1ApiPortal.viewmodel';
import { AuthenticationResponse } from 'data/schemas/auth';
import { ApiPortalFile } from 'data/schemas/apiSpec';
import { dbDateTimeToDisplayString } from 'commons/common';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    spec: ApiSpecState | null;
    employees: AuthenticationResponse[];
    specFiles?: ApiPortalFile[];
    onUploadFiles?: (files: File[]) => void;
}

function Field({ label, value, fullWidth }: { label: string; value: string; fullWidth?: boolean }) {
    return (
        <div className={`flex flex-col gap-1 ${fullWidth ? 'col-span-2' : ''}`}>
            <span className="text-xs font-bold uppercase tracking-wide text-gray-500">{label}</span>
            <span className="rounded bg-gray-50 px-2 py-1.5 text-sm text-gray-800">
                {value || <span className="italic text-gray-400">—</span>}
            </span>
        </div>
    );
}

function StatusBadge({ status }: { status: string }) {
    const isActive = status === 'active';
    return (
        <div className="flex flex-col gap-1">
            <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Status</span>
            <span
                className={twMerge(
                    'inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                    isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                )}
            >
                <span
                    className={twMerge(
                        'mr-1.5 h-1.5 w-1.5 rounded-full',
                        isActive ? 'bg-green-500' : 'bg-gray-400'
                    )}
                />
                {status}
            </span>
        </div>
    );
}

function ModalApiSpecDetail({ isOpen, onClose, spec, employees, specFiles, onUploadFiles }: Props) {
    const [files, setFiles] = useState<File[]>([]);
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleClose = useCallback(() => {
        setFiles([]);
        onClose();
    }, [onClose]);

    console.log(specFiles);

    const ownerName =
        employees.find(e => e.ctizenID === spec?.owner)?.nickName ?? spec?.owner ?? '—';

    const handleDroppedFiles = useCallback((incoming: FileList | File[]) => {
        const newFiles = Array.from(incoming);
        setFiles(prev => {
            const existingNames = new Set(prev.map(f => f.name));
            return [...prev, ...newFiles.filter(f => !existingNames.has(f.name))];
        });
    }, []);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(true);
    };
    const handleDragLeave = () => setDragging(false);
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        if (e.dataTransfer.files?.length) {
            handleDroppedFiles(e.dataTransfer.files);
            e.dataTransfer.clearData();
        }
    };

    return (
        <Modal
            isCentered
            size="4xl"
            isOpen={isOpen}
            onClose={handleClose}
            scrollBehavior="inside"
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader className="border-b">
                    <span className="text-base font-bold text-gray-800">API Spec Detail</span>
                    {spec?.api_name && (
                        <span className="ml-2 text-sm font-normal text-gray-500">
                            — {spec.api_name}
                        </span>
                    )}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {spec && (
                        <div className="flex flex-col gap-5 py-2">
                            {/* Detail fields */}
                            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                                <Field
                                    label="API Group"
                                    value={spec.api_group}
                                />
                                <Field
                                    label="Module"
                                    value={spec.module}
                                />
                                <Field
                                    label="API Name"
                                    value={spec.api_name}
                                />
                                <Field
                                    label="Namespace"
                                    value={spec.api_namespace}
                                />
                                <Field
                                    label="Class Name"
                                    value={spec.classname}
                                />
                                <Field
                                    label="Function Name"
                                    value={spec.function_name}
                                />
                                <Field
                                    label="Owner"
                                    value={ownerName}
                                />
                                <StatusBadge status={spec.status} />
                                <Field
                                    label="Updated By"
                                    value={spec.updated_by}
                                />
                                <Field
                                    label="Updated On"
                                    value={dbDateTimeToDisplayString(spec.updated_on ?? '')}
                                />
                                <Field
                                    label="Remark"
                                    value={spec.remark}
                                    fullWidth
                                />
                            </div>

                            {/* Saved Files */}
                            {specFiles!.length > 0 && (
                                <div className="border-t pt-4">
                                    <p className="mb-2 text-sm font-bold text-gray-700">
                                        Uploaded Files ({specFiles!.length})
                                    </p>
                                    <ul className="space-y-1 text-sm">
                                        {specFiles!.map((file, i) => (
                                            <li
                                                key={i}
                                                className="flex items-center justify-between rounded bg-gray-50 px-3 py-1.5"
                                            >
                                                <div className="flex min-w-0 items-center gap-2">
                                                    <FaFile
                                                        className="shrink-0 text-blue-400"
                                                        size={12}
                                                    />
                                                    <span className="truncate text-gray-700">
                                                        {file.file_name}
                                                    </span>
                                                </div>
                                                <div className="ml-3 shrink-0 text-right text-xs text-gray-400">
                                                    <span>{file.updated_by}</span>
                                                    <span className="mx-1">·</span>
                                                    <span>
                                                        {dbDateTimeToDisplayString(file.updated_on)}
                                                    </span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* File Upload */}
                            <div className="border-t pt-4">
                                <p className="mb-2 text-sm font-bold text-gray-700">Attachments</p>
                                <div
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    className={twMerge(
                                        'cursor-pointer rounded-md border border-dashed border-neutral-400 p-6 transition-colors hover:shadow-md hover:shadow-neutral-300',
                                        dragging && 'bg-sky-50'
                                    )}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <AiOutlineCloudUpload
                                            className="text-sky-500"
                                            size={28}
                                        />
                                        <div className="flex flex-col">
                                            <span className="text-sm text-neutral-500">
                                                Drop files or{' '}
                                                <span className="font-bold text-sky-500 underline">
                                                    Browse
                                                </span>
                                            </span>
                                            {files.length > 0 && (
                                                <span className="text-xs text-orange-500">
                                                    {files.length} file(s) selected
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    className="hidden"
                                    onChange={e => {
                                        if (e.target.files) handleDroppedFiles(e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                                {files.length > 0 && (
                                    <ul className="mt-3 space-y-1 text-sm">
                                        {files.map((file, i) => (
                                            <li
                                                key={i}
                                                className="flex items-center justify-between rounded bg-gray-50 px-3 py-1.5"
                                            >
                                                <span className="truncate text-gray-700">
                                                    {file.name}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setFiles(prev =>
                                                            prev.filter((_, idx) => idx !== i)
                                                        );
                                                    }}
                                                    className="ml-3 text-red-400 hover:text-red-600"
                                                >
                                                    <FaTrash size={11} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="gap-2">
                    {files.length > 0 && onUploadFiles && (
                        <button
                            onClick={() => {
                                onUploadFiles(files);
                                handleClose();
                            }}
                            className="flex items-center rounded bg-blue-50 px-3 py-1.5 text-sm text-blue-600 transition-colors hover:bg-blue-100"
                        >
                            Upload ({files.length})
                        </button>
                    )}
                    <button
                        onClick={handleClose}
                        className="flex items-center rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-200"
                    >
                        Close
                    </button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default ModalApiSpecDetail;
